import React, { useEffect, useState } from "react";
import api from "../services/apiService";
import { Helmet } from "react-helmet";

const emptyFormData = {
  id: null,
  name: "",
  prefix: "",
  nextValue: 1,
  padding: 4,
  description: "",
  resetPeriod: "none",
};

const resetPeriodOptions = [
  { value: "none", label: "Kein Reset" },
  { value: "yearly", label: "Jaehrlich" },
  { value: "monthly", label: "Monatlich" },
];

const NumberSequenceForm = () => {
  const [sequences, setSequences] = useState([]);
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [formData, setFormData] = useState(emptyFormData);

  const isInvoiceSequence = formData.name === "Invoice";
  const previewYear = new Date().getFullYear();

  useEffect(() => {
    loadSequences();
  }, []);

  const loadSequences = () => {
    api
      .get("/numbersequence")
      .then((response) => setSequences(response.data))
      .catch((error) => console.error("Fehler beim Abrufen der Nummernkreise:", error));
  };

  const handleSelectChange = (e) => {
    const selectedId = e.target.value;

    if (selectedId === "") {
      setSelectedSequence(null);
      setFormData(emptyFormData);
      return;
    }

    const sequence = sequences.find((seq) => seq.id === parseInt(selectedId, 10));
    setSelectedSequence(sequence || null);
    setFormData(sequence ? { ...sequence } : emptyFormData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDelete = (id) => {
    if (!window.confirm("Moechten Sie diesen Nummernkreis wirklich loeschen?")) {
      return;
    }

    api
      .delete(`/numbersequence/${id}`)
      .then(() => {
        alert("Nummernkreis erfolgreich geloescht!");
        loadSequences();
        setSelectedSequence(null);
        setFormData(emptyFormData);
      })
      .catch((error) => console.error("Fehler beim Loeschen:", error));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const path = formData.id ? `/numbersequence/${formData.id}` : "/numbersequence";
    const method = formData.id ? "put" : "post";
    const payload = isInvoiceSequence
      ? { ...formData, prefix: "", resetPeriod: "none" }
      : formData;

    api[method](path, payload)
      .then(() => {
        alert("Nummernkreis erfolgreich gespeichert!");
        setSelectedSequence(null);
        setFormData(emptyFormData);
        loadSequences();
      })
      .catch((error) => {
        console.error("Fehler beim Speichern:", error);
        alert(`Speicher-Vorgang gescheitert: ${error.response?.statusText || error.message}`);
      });
  };

  const getPreview = () => {
    const paddedNumber = String(formData.nextValue).padStart(Number(formData.padding), "0");

    if (isInvoiceSequence) {
      return `${previewYear}-${paddedNumber}`;
    }

    return `${formData.prefix || ""}${paddedNumber}`;
  };

  return (
    <div>
      <Helmet>
        <title>Nummernkreis-Verwaltung</title>
        <meta name="description" content="Nummernkreis-Verwaltung" />
      </Helmet>

      <h1>Nummernkreis-Verwaltung</h1>

      <div style={{ display: "flex", alignItems: "center", marginBottom: "20px", maxWidth: "500px", margin: "0 auto" }}>
        <label htmlFor="sequence-select" style={{ marginRight: "10px", width: "200px" }}>
          Nummernkreis auswaehlen:
        </label>
        <select
          id="sequence-select"
          value={selectedSequence?.id || ""}
          onChange={handleSelectChange}
          style={{
            width: "280px",
            padding: "5px",
            borderRadius: "4px",
            border: "1px solid #ccc",
          }}
        >
          <option value="" disabled>
            Neuen Nummernkreis erstellen
          </option>
          {sequences.map((sequence) => (
            <option key={sequence.id} value={sequence.id}>
              {sequence.name}
            </option>
          ))}
        </select>
      </div>

      <form onSubmit={handleSubmit}>
        <div>
          <label>
            Name des Nummernkreises:
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={!!formData.id}
            />
          </label>
        </div>

        <div>
          <label>
            Praefix:
            {isInvoiceSequence ? (
              <div style={{ marginTop: "6px" }}>
                Das Praefix wird bei Rechnungen automatisch aus dem Jahr gebildet, z. B. {previewYear}-
              </div>
            ) : (
              <input
                type="text"
                name="prefix"
                value={formData.prefix}
                onChange={handleChange}
              />
            )}
          </label>
        </div>

        <div>
          <label>
            Startnummer:
            <input
              type="number"
              name="nextValue"
              value={formData.nextValue}
              onChange={handleChange}
              required
            />
          </label>
        </div>

        <div>
          <label>
            Anzahl fuehrender Nullen:
            <input
              type="number"
              name="padding"
              value={formData.padding}
              onChange={handleChange}
              required
            />
          </label>
        </div>

        <div>
          <label>
            Beschreibung:
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
            />
          </label>
        </div>

        <div>
          <label>
            Reset-Periode:
            {isInvoiceSequence ? (
              <div style={{ marginTop: "6px" }}>
                Fuer Rechnungen bleibt der Zaehler ohne Reset aktiv, damit die laufende Nummer jahresuebergreifend weiterzaehlt.
              </div>
            ) : (
              <select
                name="resetPeriod"
                value={formData.resetPeriod}
                onChange={handleChange}
              >
                {resetPeriodOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
          </label>
        </div>

        <div>
          <strong>Vorschau der naechsten Nummer: </strong>
          <span>{getPreview()}</span>
        </div>

        <button type="submit">
          {formData.id ? "Aktualisieren" : "Erstellen"}
        </button>

        {formData.id && (
          <>
            <button
              type="button"
              onClick={() => {
                setSelectedSequence(null);
                setFormData(emptyFormData);
              }}
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={() => handleDelete(formData.id)}
            >
              Nummernkreis loeschen
            </button>
          </>
        )}
      </form>
    </div>
  );
};

export default NumberSequenceForm;
